import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  lazy,
  Suspense,
} from 'react';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  animate as fmAnimate,
} from 'framer-motion';
import {
  Home,
  Heart,
  CalendarHeart,
  Image as ImageIcon,
  MapPin,
  Navigation,
  Sparkles,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Gem,
  Flower2,
  Clock,
  Camera,
  X,
} from 'lucide-react';

import { usePerfTier } from './three/usePerfTier';
import { startViewerTracking } from './three/store';
import SpotlightCard from './components/SpotlightCard';
import ShareInvite from './components/ShareInvite';

/* Three.js is by far the heaviest thing on the page. Splitting it out lets
   the type, the countdown and the layout paint first; the 3D fades in behind
   them a moment later. */
const AmbientScene = lazy(() => import('./three/AmbientScene'));
const HeroRings = lazy(() => import('./three/HeroRings'));
const HeartSculpture = lazy(() => import('./three/HeartSculpture'));

/** Mounts a lazy 3D canvas, but only when the device tier says it's worth it. */
function Canvas3D({ tier, children }) {
  if (!tier || tier === 'off') return null;
  return <Suspense fallback={null}>{children}</Suspense>;
}

/* ------------------------------------------------------------------ */
/*  Config — edit these to update the whole site                       */
/* ------------------------------------------------------------------ */

const WEDDING = {
  groom: 'Ranjith',
  bride: 'Hovarthini',
  engagementDateTime: '2026-09-12T18:00:00+05:30',
  weddingDateTime: '2026-09-13T08:00:00+05:30',
  venueName: 'Hindu Varthagarhal Sangam Marriage Hall',
  venueAddress: 'Natham, Dindigul, Tamil Nadu',
  mapsEmbedSrc:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.5087221090585!2d78.22427091062232!3d10.220491969159692!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b00bcca3843c347%3A0x36d180b91108670b!2sHindu%20Varthagarhal%20Sangam%20Marriage%20Hall!5e0!3m2!1sen!2sin!4v1783851181947!5m2!1sen!2sin',
};

/** Opens the phone's map app with the route already set. */
const MAPS_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  `${WEDDING.venueName}, ${WEDDING.venueAddress}`
)}`;

// Free, no-auth, CORS-enabled hit-counter service — keeps the wish count shared across every visitor.
// Swap this out for your own backend later if you want the count to be more durable/tamper-proof.
const WISH_NAMESPACE = 'ranjith-hovarthini-wedding-2026';
const WISH_KEY = 'love-taps';
const WISH_GET_URL = `https://abacus.jasoncameron.dev/get/${WISH_NAMESPACE}/${WISH_KEY}`;
const WISH_HIT_URL = `https://abacus.jasoncameron.dev/hit/${WISH_NAMESPACE}/${WISH_KEY}`;

/** The two families, as they should be read on the invitation. */
const FAMILIES = [
  {
    side: 'The Groom',
    relation: 'Son of',
    child: 'Ranjith',
    father: 'Sri. R.S. Sevugarajan',
    mother: 'Smt. S. Radha',
  },
  {
    side: 'The Bride',
    relation: 'Daughter of',
    child: 'Hovarthini',
    father: 'Sri. G. Ramani',
    mother: 'Smt. R. Uma Kamatchi',
  },
];

/**
 * The order of the day. Only the two confirmed functions are listed — add a
 * reception, sangeet or mehendi here and it slots straight into the timeline.
 */
const EVENTS = [
  {
    name: 'Nichayathartham',
    english: 'Engagement',
    day: 'Saturday, 12 September 2026',
    time: '6:00 – 8:00 PM',
    note: 'Rings exchanged and the promise blessed in the presence of both families.',
    icon: Gem,
  },
  {
    name: 'Muhurtham',
    english: 'The Wedding',
    day: 'Sunday, 13 September 2026',
    time: '8:00 – 9:00 AM',
    note: 'The ceremony itself, at the auspicious hour — the moment we become one family.',
    icon: Flower2,
  },
];

/**
 * The official Save-the-Date card — the couple on the oonjal swing, names
 * and date set by the designer. Exported for the web by
 * scripts/prepare-gallery.ps1.
 */
const PUB = process.env.PUBLIC_URL || '';
const FEATURED_PHOTO = {
  src: `${PUB}/gallery/moment.jpg`,
  w: 1067,
  h: 1600,
  alt: 'Save the Date — Ranjith & Hovarthini, 13 September 2026',
};

const NAV_ITEMS = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'events', label: 'Events', icon: CalendarHeart },
  { id: 'invites', label: 'Invites', icon: ImageIcon },
  { id: 'moments', label: 'The Date', icon: Camera },
  { id: 'venue', label: 'Venue', icon: MapPin },
  { id: 'wishes', label: 'Wishes', icon: Sparkles },
];


/* ------------------------------------------------------------------ */
/*  Render tier                                                        */
/*  Resolved once at the top and shared, so every canvas agrees on how */
/*  much this device can afford.                                       */
/* ------------------------------------------------------------------ */

const TierContext = createContext('low');
const useTier = () => useContext(TierContext);

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

function useCountdown(targetDate) {
  const calculate = useCallback(() => {
    const diff = +new Date(targetDate) - +new Date();
    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
    }
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      isPast: false,
    };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(calculate);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calculate()), 1000);
    return () => clearInterval(timer);
  }, [calculate]);

  return timeLeft;
}

/* ------------------------------------------------------------------ */
/*  Chrome — backdrop, preloader, progress                             */
/* ------------------------------------------------------------------ */

/**
 * The full-page backdrop: a painted gradient base, the live 3D petal field
 * on top of it, then a vignette and film grain to sit the whole thing down.
 */
function Backdrop() {
  const tier = useTier();
  return (
    <div className="fixed inset-0 z-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-rose-100/50 via-ivory-100 to-rose-100/60" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_-5%,rgba(217,67,95,0.14),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_88%,rgba(201,165,74,0.16),transparent_52%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_8%_65%,rgba(240,169,182,0.18),transparent_45%)]" />

      <Canvas3D tier={tier}>
        <AmbientScene tier={tier} />
      </Canvas3D>

      {/* vignette — pulls the eye to the centre of the page */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(94,22,38,0.14)_100%)]" />
      {/* the faintest grain, so the gradients never band */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
    </div>
  );
}

/** A hairline of gold that fills as the page is read. */
function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 26, mass: 0.4 });
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed left-0 right-0 top-0 z-[80] h-[2px] origin-left bg-gradient-to-r from-rose-500 via-gold-400 to-rose-500"
    />
  );
}

/** Holds the page until the fonts land, so the names never reflow on reveal. */
function Preloader({ onDone }) {
  useEffect(() => {
    let cancelled = false;
    const minimum = new Promise((resolve) => setTimeout(resolve, 1300));
    const fonts = document.fonts?.ready ?? Promise.resolve();
    Promise.all([minimum, fonts]).then(() => {
      if (!cancelled) onDone();
    });
    return () => {
      cancelled = true;
    };
  }, [onDone]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, filter: 'blur(12px)' }}
      transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-ivory-100"
    >
      <div className="relative flex h-32 w-32 items-center justify-center">
        <svg viewBox="0 0 100 100" className="absolute h-full w-full -rotate-90">
          <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(217,189,124,0.25)" strokeWidth="1.5" />
          <motion.circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="#C9A54A"
            strokeWidth="1.5"
            strokeLinecap="round"
            pathLength={1}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.15, ease: 'easeInOut' }}
          />
        </svg>
        <motion.span
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, duration: 0.7 }}
          className="font-script text-3xl text-maroon-700"
        >
          R&nbsp;&amp;&nbsp;H
        </motion.span>
      </div>
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.7 }}
        className="mt-6 font-sans text-[10px] uppercase tracking-[0.45em] text-gold-600"
      >
        13 · 09 · 2026
      </motion.p>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared UI                                                          */
/* ------------------------------------------------------------------ */

function OrnamentDivider() {
  return (
    <div className="mx-auto my-4 flex w-44 items-center justify-center gap-3">
      <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold-400/60 to-rose-300/70" />
      <motion.span
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
        className="drop-glow-rose"
      >
        <Heart size={13} className="text-rose-500" fill="currentColor" />
      </motion.span>
      <span className="h-px flex-1 bg-gradient-to-l from-transparent via-gold-400/60 to-rose-300/70" />
    </div>
  );
}

/**
 * A calligraphic divider: two mirrored strokes that draw themselves in as
 * they scroll into view, curling toward a beating heart at the centre.
 */
function Flourish({ className = '' }) {
  const stroke = {
    initial: { pathLength: 0, opacity: 0 },
    whileInView: { pathLength: 1, opacity: 1 },
    viewport: { once: true, amount: 0.7 },
  };
  return (
    <div className={`relative mx-auto my-4 flex w-56 items-center justify-center ${className}`}>
      <svg viewBox="0 0 224 26" className="h-6 w-56 text-gold-400" fill="none" aria-hidden>
        {/* main strokes, drawn outward from the centre */}
        <motion.path
          {...stroke}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          d="M100 13 C 76 13, 52 5, 30 9 C 18 11, 10 15, 6 13"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <motion.path
          {...stroke}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          d="M124 13 C 148 13, 172 5, 194 9 C 206 11, 214 15, 218 13"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        {/* echo strokes, lighter, curving the other way */}
        <motion.path
          {...stroke}
          transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
          d="M100 13 C 78 13, 56 21, 36 18"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.45"
        />
        <motion.path
          {...stroke}
          transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
          d="M124 13 C 146 13, 168 21, 188 18"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.45"
        />
        <motion.circle
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.7 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          cx="6"
          cy="13"
          r="1.8"
          fill="currentColor"
        />
        <motion.circle
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.7 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          cx="218"
          cy="13"
          r="1.8"
          fill="currentColor"
        />
      </svg>
      <motion.span
        className="absolute drop-glow-rose"
        animate={{ scale: [1, 1.18, 1] }}
        transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
      >
        <Heart size={12} className="text-rose-500" fill="currentColor" />
      </motion.span>
    </div>
  );
}

function SectionHeading({ kicker, title }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22, rotateX: -14 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{ transformPerspective: 900 }}
      className="mx-auto mb-8 max-w-xl text-center md:mb-12"
    >
      <p className="text-gold-foil mb-2 font-sans text-xs font-medium uppercase tracking-[0.35em]">
        {kicker}
      </p>
      <h2 className="font-serif text-3xl italic text-maroon-700 drop-shadow-[0_2px_10px_rgba(94,22,38,0.12)] sm:text-4xl">
        {title}
      </h2>
      <Flourish />
    </motion.div>
  );
}

/** Small fixed monogram so the couple's mark is present on phones, where the top nav is hidden. */
function MobileMonogram() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="fixed left-1/2 top-3 z-50 -translate-x-1/2 md:hidden"
    >
      <div className="glass gold-edge flex items-center gap-1.5 rounded-full px-4 py-1 font-script text-lg text-maroon-700">
        R
        <Heart size={9} className="text-rose-500" fill="currentColor" />
        H
      </div>
    </motion.div>
  );
}

function TopNav({ active, onNavigate }) {
  return (
    <nav className="fixed top-0 z-50 hidden w-full items-center justify-center px-6 py-5 md:flex">
      <div className="glass gold-edge flex items-center gap-1 rounded-full px-2 py-2">
        {NAV_ITEMS.map((item) => {
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onNavigate(item.id)}
              className={`relative flex items-center gap-2 rounded-full px-4 py-2 font-sans text-xs tracking-wide transition-colors ${
                isActive ? 'text-rose-600' : 'text-maroon-400/80 hover:text-rose-500'
              }`}
            >
              {isActive && (
                <motion.span
                  layoutId="topNavActive"
                  className="absolute inset-0 rounded-full bg-gradient-to-b from-rose-100/90 to-rose-300/40 shadow-gold"
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.55 }}
                />
              )}
              <item.icon size={14} className="relative z-10" />
              <span className="relative z-10">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function BottomNav({ active, onNavigate }) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 flex justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:hidden">
      <div className="glass-deep gold-edge flex w-full max-w-md items-center justify-between rounded-2xl px-2 py-2">
        {NAV_ITEMS.map((item) => {
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onNavigate(item.id)}
              aria-label={item.label}
              className="relative flex flex-1 flex-col items-center gap-1 rounded-xl py-2"
            >
              {isActive && (
                <motion.span
                  layoutId="bottomNavActive"
                  className="absolute inset-0 rounded-xl bg-gradient-to-b from-rose-100/90 to-rose-300/45"
                  transition={{ type: 'spring', bounce: 0.25, duration: 0.5 }}
                />
              )}
              <motion.span
                animate={isActive ? { scale: [1, 1.25, 1] } : { scale: 1 }}
                transition={{ duration: 0.4 }}
                className="relative z-10"
              >
                <item.icon
                  size={20}
                  className={isActive ? 'text-rose-600' : 'text-maroon-400'}
                  fill={isActive && item.icon === Heart ? 'currentColor' : 'none'}
                />
              </motion.span>
              <span
                className={`relative z-10 font-sans text-[10px] ${
                  isActive ? 'text-rose-600' : 'text-maroon-400'
                }`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero                                                                */
/* ------------------------------------------------------------------ */

function Hero({ onNavigate }) {
  const tier = useTier();
  const countdown = useCountdown(WEDDING.weddingDateTime);
  const units = [
    { label: 'Days', short: 'D', value: countdown.days },
    { label: 'Hours', short: 'H', value: countdown.hours },
    { label: 'Minutes', short: 'M', value: countdown.minutes },
    { label: 'Seconds', short: 'S', value: countdown.seconds },
  ];

  // The hero dissolves as it leaves rather than being cut off by the fold —
  // opacity only, so the absolutely-positioned canvas keeps its geometry.
  const { scrollY } = useScroll();
  const dissolve = useTransform(scrollY, [0, 520], [1, 0]);

  return (
    <motion.section
      id="home"
      style={{ opacity: dissolve }}
      className="relative flex min-h-screen scroll-mt-24 flex-col items-center justify-center px-6 pb-24 pt-16 text-center md:scroll-mt-28 md:pb-24 md:pt-28"
    >
      {/* the interlocked bands turn behind the names */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[340px] w-[340px] -translate-x-1/2 -translate-y-[58%] xs:h-[400px] xs:w-[400px] sm:h-[560px] sm:w-[560px] md:h-[620px] md:w-[620px]">
        <Canvas3D tier={tier}>
          <HeroRings tier={tier} />
        </Canvas3D>
      </div>
      {/* scrim: keeps the headline legible wherever the bands happen to be */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[420px] w-[520px] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,rgba(255,253,249,0.72),rgba(255,253,249,0)_68%)]" />

      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-4 flex items-center gap-2 font-sans text-xs uppercase tracking-[0.4em] text-rose-500"
      >
        <Heart size={11} fill="currentColor" /> Together with their families{' '}
        <Heart size={11} fill="currentColor" />
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.7 }}
        style={{ transformPerspective: 1200 }}
        className="font-script text-4xl leading-tight text-maroon-700 drop-shadow-[0_4px_24px_rgba(255,253,249,0.9)] xs:text-5xl sm:text-7xl md:text-8xl"
      >
        <motion.span
          initial={{ opacity: 0, x: -24, rotateY: 35 }}
          animate={{ opacity: 1, x: 0, rotateY: 0 }}
          transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block"
        >
          {WEDDING.groom}
        </motion.span>
        <motion.span
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, type: 'spring', bounce: 0.5 }}
          className="text-gold-foil mx-2 inline-block font-serif text-2xl italic sm:mx-3 sm:text-5xl"
        >
          &amp;
        </motion.span>
        <br className="sm:hidden" />
        <motion.span
          initial={{ opacity: 0, x: 24, rotateY: -35 }}
          animate={{ opacity: 1, x: 0, rotateY: 0 }}
          transition={{ delay: 0.45, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block"
        >
          {WEDDING.bride}
        </motion.span>
      </motion.h1>

      <Flourish />

      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="mt-4 max-w-xl font-serif text-lg italic text-maroon-600/90 sm:text-xl"
      >
        "Our love story turns a new page."
      </motion.p>

      {countdown.isPast ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.55, type: 'spring', bounce: 0.4 }}
          className="glass gold-edge mt-10 rounded-2xl px-8 py-5 sm:mt-12"
        >
          <p className="font-serif text-xl italic text-maroon-700 sm:text-2xl">
            The wait is over — we are married!
          </p>
          <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.3em] text-gold-600">
            13 · 09 · 2026 · Natham
          </p>
        </motion.div>
      ) : (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.6 }}
        className="mt-10 flex items-stretch justify-center gap-2 xs:gap-3 sm:mt-12 sm:gap-4"
        style={{ transformStyle: 'preserve-3d', transformPerspective: 900 }}
      >
        {units.map((unit, i) => (
          <motion.div
            key={unit.label}
            initial={{ rotateX: -25, opacity: 0 }}
            animate={{ rotateX: 0, opacity: 1 }}
            transition={{ delay: 0.6 + i * 0.07, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            className="glass gold-edge flex w-14 flex-col items-center rounded-2xl py-3 xs:w-16 sm:w-20 sm:py-4"
            style={{ transformPerspective: 900 }}
          >
            <AnimatePresence mode="popLayout">
              <motion.span
                key={unit.value}
                initial={{ opacity: 0, y: -12, rotateX: -60 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, y: 12, rotateX: 60 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif text-2xl font-semibold text-maroon-700 xs:text-3xl sm:text-5xl"
              >
                {String(unit.value).padStart(2, '0')}
              </motion.span>
            </AnimatePresence>
            <span className="mt-1 font-sans text-[8px] uppercase tracking-[0.15em] text-gold-600 xs:text-[9px] xs:tracking-[0.2em] sm:mt-2 sm:text-xs">
              <span className="xs:hidden">{unit.short}</span>
              <span className="hidden xs:inline">{unit.label}</span>
            </span>
          </motion.div>
        ))}
      </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.75, duration: 0.6 }}
        className="mt-10 flex flex-col items-center gap-1 font-sans text-sm text-maroon-600"
      >
        <span>September 13th, 2026 · 8:00 – 9:00 AM</span>
        <span className="text-maroon-400">
          {WEDDING.venueName}, {WEDDING.venueAddress}
        </span>
      </motion.div>

      {/* every landing needs a next step — the page's two real intents */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="mt-8 flex flex-wrap items-center justify-center gap-3"
      >
        <motion.button
          type="button"
          onClick={() => onNavigate('invites')}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.96 }}
          className="btn-gold rounded-full px-7 py-3 font-sans text-xs uppercase tracking-[0.25em]"
        >
          <ImageIcon size={14} />
          Open the Invitation
        </motion.button>
        <motion.a
          href={MAPS_DIRECTIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.96 }}
          className="glass gold-edge flex items-center gap-2 rounded-full px-7 py-3 font-sans text-xs uppercase tracking-[0.25em] text-maroon-700 transition-shadow hover:shadow-gold"
        >
          <Navigation size={14} className="text-rose-500" />
          Get Directions
        </motion.a>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 1.8 }}
        className="absolute bottom-8 hidden flex-col items-center text-rose-400 md:flex"
      >
        <ChevronDown size={22} />
      </motion.div>
    </motion.section>
  );
}

/* ------------------------------------------------------------------ */
/*  Blessings & Events                                                  */
/* ------------------------------------------------------------------ */

/** One family's panel. The pair fold toward each other like an open card. */
function FamilyPanel({ family, fold, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className={fold}>
        <SpotlightCard className="rounded-3xl" glow={320}>
          <div className="glass gold-edge rounded-3xl px-6 py-8 text-center sm:px-8">
            <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-gold-600">
              {family.side}
            </p>

            <p className="mt-4 font-script text-4xl text-maroon-700 drop-shadow-[0_2px_12px_rgba(255,253,249,0.9)] sm:text-5xl">
              {family.child}
            </p>

            <div className="mx-auto my-5 flex w-28 items-center justify-center gap-2">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold-400/60" />
              <Heart size={9} className="text-rose-400" fill="currentColor" />
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold-400/60" />
            </div>

            <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-rose-500">
              {family.relation}
            </p>
            <p className="mt-3 font-serif text-lg italic leading-relaxed text-maroon-700 sm:text-xl">
              {family.father}
              <br />
              <span className="font-sans text-xs not-italic tracking-[0.3em] text-gold-600">
                &amp;
              </span>
              <br />
              {family.mother}
            </p>
          </div>
        </SpotlightCard>
      </div>
    </motion.div>
  );
}

function EventCard({ event, index }) {
  const Icon = event.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: -24, rotateY: 12 }}
      whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      style={{ transformPerspective: 1000 }}
      className="relative flex gap-4 pl-10 sm:gap-6 sm:pl-12"
    >
      <motion.div
        whileInView={{ scale: [0.6, 1.15, 1] }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 0.5, delay: index * 0.08 }}
        className="glass absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-rose-300 sm:h-10 sm:w-10"
      >
        <Icon size={15} className="text-rose-500 drop-glow-rose" />
      </motion.div>

      <SpotlightCard className="flex-1 rounded-2xl" glow={260}>
        <div className="glass gold-edge rounded-2xl p-4 transition-shadow duration-500 hover:shadow-gold sm:p-5">
          <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="font-serif text-lg font-semibold italic text-maroon-700 sm:text-xl">
              {event.name}
            </p>
            <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-gold-600">
              {event.english}
            </span>
          </div>

          <div className="flex flex-col gap-1 font-sans text-sm text-maroon-600 xs:flex-row xs:items-center xs:gap-3">
            <span>{event.day}</span>
            <span className="hidden text-gold-400 xs:inline">·</span>
            <span className="flex items-center gap-1.5 text-rose-500">
              <Clock size={13} />
              {event.time}
            </span>
          </div>

          <p className="mt-2 font-sans text-sm text-maroon-500">{event.note}</p>
        </div>
      </SpotlightCard>
    </motion.div>
  );
}

function Blessings() {
  return (
    <section id="events" className="relative scroll-mt-24 px-6 py-14 md:scroll-mt-28 md:py-32">
      <SectionHeading kicker="With The Blessings Of" title="Our Two Families" />

      <div className="relative mx-auto max-w-4xl" style={{ perspective: 1600 }}>
        <div className="grid gap-6 preserve-3d sm:grid-cols-2 sm:gap-10">
          <FamilyPanel family={FAMILIES[0]} fold="fold-left" delay={0} />
          <FamilyPanel family={FAMILIES[1]} fold="fold-right" delay={0.12} />
        </div>

        {/* the clasp where the two halves of the card meet */}
        <motion.div
          initial={{ opacity: 0, scale: 0.4 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ delay: 0.35, type: 'spring', bounce: 0.45, duration: 0.8 }}
          className="pointer-events-none absolute left-1/2 top-1/2 hidden h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-br from-gold-100 via-gold-300 to-gold-500 shadow-gold sm:flex"
        >
          <Heart size={20} className="animate-heartbeat text-maroon-700" fill="currentColor" />
        </motion.div>
      </div>

      <div className="mx-auto mt-16 max-w-2xl md:mt-24">
        <SectionHeading kicker="Do Join Us" title="Order Of The Day" />
        <div className="relative">
          <div className="absolute bottom-2 left-[15px] top-2 w-px bg-gradient-to-b from-rose-300 via-gold-300/60 to-rose-400 sm:left-[19px]" />
          <div className="space-y-8">
            {EVENTS.map((event, i) => (
              <EventCard key={event.name} event={event} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Assets — Invitations                                               */
/* ------------------------------------------------------------------ */

const REVEAL_DISTANCE = 600;

function InvitationCard({ title, initials, content }) {
  const [revealed, setRevealed] = useState(false);
  const x = useMotionValue(0);

  const reveal = () => {
    fmAnimate(x, REVEAL_DISTANCE, {
      type: 'spring',
      stiffness: 260,
      damping: 28,
      onComplete: () => setRevealed(true),
    });
  };

  const coverAgain = () => {
    x.set(REVEAL_DISTANCE);
    setRevealed(false);
    requestAnimationFrame(() => {
      fmAnimate(x, 0, { type: 'spring', stiffness: 260, damping: 28 });
    });
  };

  const handleDragEnd = (_event, info) => {
    if (info.offset.x > 90 || info.velocity.x > 500) {
      reveal();
    } else {
      fmAnimate(x, 0, { type: 'spring', stiffness: 400, damping: 32 });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      reveal();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 36, rotateX: -16 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      style={{ transformPerspective: 1200 }}
    >
      <SpotlightCard className="rounded-3xl" glow={340} lift={6}>
        <div className="glass gold-edge group relative rounded-3xl p-3 transition-shadow duration-500 hover:shadow-gold">
          {/* note: this pane clips, which flattens 3D — depth here comes from
              the tilt and sheen rather than translated layers */}
          <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-rose-100/60 via-ivory-50/80 to-ivory-200">
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
              {content}
            </div>

            {/* the double hairline + corner diamonds of a printed card */}
            <span aria-hidden className="pointer-events-none absolute inset-0">
              <span className="absolute inset-3 rounded-xl border border-gold-400/50" />
              <span className="absolute inset-[18px] rounded-lg border border-gold-300/30" />
              <span className="absolute left-[9px] top-[9px] h-1.5 w-1.5 rotate-45 bg-gold-400/80" />
              <span className="absolute right-[9px] top-[9px] h-1.5 w-1.5 rotate-45 bg-gold-400/80" />
              <span className="absolute bottom-[9px] left-[9px] h-1.5 w-1.5 rotate-45 bg-gold-400/80" />
              <span className="absolute bottom-[9px] right-[9px] h-1.5 w-1.5 rotate-45 bg-gold-400/80" />
            </span>

            {revealed && (
              <button
                type="button"
                onClick={coverAgain}
                aria-label={`Cover ${title} again`}
                className="glass absolute right-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-full text-rose-500 transition-transform hover:scale-110"
              >
                <RotateCcw size={15} />
              </button>
            )}

            {!revealed && (
              <motion.div
                style={{ x }}
                drag="x"
                dragConstraints={{ left: -20, right: REVEAL_DISTANCE }}
                dragElastic={0.15}
                onDragEnd={handleDragEnd}
                onTap={reveal}
                role="button"
                tabIndex={0}
                aria-label={`Swipe or tap to reveal ${title}`}
                onKeyDown={handleKeyDown}
                className="absolute inset-0 z-10 flex touch-none cursor-grab select-none flex-col items-center justify-center gap-4 overflow-hidden rounded-2xl border border-rose-200/60 bg-gradient-to-br from-ivory-100 via-rose-100/70 to-ivory-200 active:cursor-grabbing"
              >
                {/* a slow light sweep across the sealed cover */}
                <motion.span
                  aria-hidden
                  animate={{ x: ['-120%', '220%'] }}
                  transition={{ repeat: Infinity, duration: 3.6, ease: 'easeInOut', repeatDelay: 1.2 }}
                  className="pointer-events-none absolute inset-y-0 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/70 to-transparent"
                />
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                  className="relative flex h-16 w-16 items-center justify-center whitespace-nowrap rounded-full border border-rose-400 font-script text-xl text-maroon-700 shadow-gold"
                >
                  {initials}
                  <Heart
                    size={12}
                    className="absolute -right-1 -top-1 animate-heartbeat text-rose-500"
                    fill="currentColor"
                  />
                </motion.div>
                <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-rose-500">
                  Swipe to Reveal
                </p>
                <motion.div
                  animate={{ x: [0, 10, 0] }}
                  transition={{ repeat: Infinity, duration: 1.4, ease: 'easeInOut' }}
                  className="flex text-rose-400"
                >
                  <ChevronRight size={16} />
                  <ChevronRight size={16} className="-ml-2 opacity-60" />
                </motion.div>
              </motion.div>
            )}
          </div>
          <div className="flex items-center justify-between px-2 py-4 font-serif italic text-maroon-700">
            <span>{title}</span>
            <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-gold-600">
              R &amp; H
            </span>
          </div>
        </div>
      </SpotlightCard>
    </motion.div>
  );
}

function InvitationBody({ occasion, date, time }) {
  return (
    <>
      <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold-600">
        With Joy In Our Hearts
      </p>
      <p className="font-script text-3xl text-maroon-700">
        {WEDDING.groom} &amp; {WEDDING.bride}
      </p>
      <span className="h-px w-14 bg-gold-400/60" />
      <div>
        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-gold-600">{occasion}</p>
        <p className="font-serif text-xl italic text-maroon-700">{date}</p>
        <p className="font-sans text-xs text-maroon-500">{time}</p>
      </div>
      <span className="h-px w-14 bg-gold-400/60" />
      <p className="font-sans text-[11px] leading-relaxed text-maroon-500">{WEDDING.venueName}</p>
    </>
  );
}

function Assets() {
  return (
    <section id="invites" className="relative scroll-mt-24 px-6 py-14 md:scroll-mt-28 md:py-32">
      <SectionHeading kicker="Keepsakes" title="Our Invitations" />
      <div className="mx-auto grid max-w-4xl gap-8 sm:grid-cols-2">
        <InvitationCard
          title="Engagement"
          initials="R & H"
          content={
            <InvitationBody
              occasion="Engagement"
              date="12th September 2026"
              time="6:00 – 8:00 PM"
            />
          }
        />
        <InvitationCard
          title="Wedding"
          initials="R & H"
          content={
            <InvitationBody occasion="Wedding" date="13th September 2026" time="8:00 – 9:00 AM" />
          }
        />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Moments — the pre-shoot gallery                                     */
/* ------------------------------------------------------------------ */

function Lightbox({ photo, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-maroon-800/80 p-4 backdrop-blur-md sm:p-8"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close photo"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-ivory-50/30 bg-ivory-50/10 text-ivory-50 backdrop-blur-md transition-colors hover:bg-ivory-50/25"
      >
        <X size={20} />
      </button>

      <motion.figure
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-full flex-col items-center"
      >
        <img
          src={photo.src}
          alt={photo.alt}
          width={photo.w}
          height={photo.h}
          className="max-h-[80vh] w-auto max-w-full rounded-xl shadow-2xl"
        />
        <figcaption className="mt-4 font-serif text-sm italic text-ivory-100/90">
          {photo.alt}
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}

function Moments() {
  const [open, setOpen] = useState(false);

  return (
    <section id="moments" className="relative scroll-mt-24 px-6 py-14 md:scroll-mt-28 md:py-32">
      <SectionHeading kicker="Mark the Day" title="Save the Date" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto max-w-sm sm:max-w-md"
      >
        <SpotlightCard className="rounded-3xl" glow={320} lift={6}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={`View photo: ${FEATURED_PHOTO.alt}`}
            className="glass gold-edge group block w-full cursor-zoom-in rounded-3xl p-3"
          >
            <span className="relative block overflow-hidden rounded-2xl">
              <img
                src={FEATURED_PHOTO.src}
                alt={FEATURED_PHOTO.alt}
                width={FEATURED_PHOTO.w}
                height={FEATURED_PHOTO.h}
                loading="lazy"
                decoding="async"
                className="h-auto w-full transform-gpu transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              />
              {/* the same double hairline the invitation cards wear */}
              <span aria-hidden className="pointer-events-none absolute inset-0">
                <span className="absolute inset-3 rounded-xl border border-ivory-50/45" />
                <span className="absolute left-[9px] top-[9px] h-1.5 w-1.5 rotate-45 bg-gold-300/90" />
                <span className="absolute right-[9px] top-[9px] h-1.5 w-1.5 rotate-45 bg-gold-300/90" />
                <span className="absolute bottom-[9px] left-[9px] h-1.5 w-1.5 rotate-45 bg-gold-300/90" />
                <span className="absolute bottom-[9px] right-[9px] h-1.5 w-1.5 rotate-45 bg-gold-300/90" />
              </span>
            </span>
            <span className="flex items-center justify-between px-2 py-3 font-serif italic text-maroon-700">
              <span>With all our love</span>
              <span className="flex shrink-0 items-center gap-1.5 font-sans text-[10px] not-italic uppercase tracking-[0.25em] text-gold-600">
                13 · 09 · 2026
              </span>
            </span>
          </button>
        </SpotlightCard>
      </motion.div>

      <p className="mt-6 text-center font-sans text-xs text-maroon-400">
        Tap the photograph to see it up close.
      </p>

      <AnimatePresence>
        {open && <Lightbox photo={FEATURED_PHOTO} onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Venue                                                               */
/* ------------------------------------------------------------------ */

/** A little pin that rotates in perspective — cheap depth, no extra canvas. */
function SpinningPin() {
  return (
    <motion.div
      animate={{ rotateY: 360 }}
      transition={{ repeat: Infinity, duration: 9, ease: 'linear' }}
      style={{ transformStyle: 'preserve-3d', transformPerspective: 600 }}
      className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-gold-200 via-gold-400 to-gold-600 shadow-gold"
    >
      <MapPin size={18} className="text-ivory-50" />
    </motion.div>
  );
}

function Location() {
  return (
    <section id="venue" className="relative scroll-mt-24 px-6 py-14 md:scroll-mt-28 md:py-32">
      <SectionHeading kicker="Join Us At" title="Venue Details" />
      <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-2 lg:items-start">
        <motion.div
          initial={{ opacity: 0, x: -26, rotateY: 14 }}
          whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformPerspective: 1200 }}
        >
          <SpotlightCard className="rounded-3xl" glow={320}>
            <div className="glass gold-edge rounded-3xl p-6 sm:p-8">
              <div className="mb-5 flex items-center gap-4">
                <SpinningPin />
                <div>
                  <p className="font-serif text-2xl italic text-maroon-700">{WEDDING.venueName}</p>
                  <p className="mt-1 font-sans text-sm text-maroon-500">{WEDDING.venueAddress}</p>
                </div>
              </div>

              <div className="my-6 h-px w-full bg-gradient-to-r from-gold-400/70 via-rose-300/40 to-transparent" />

              <dl className="space-y-3 font-sans text-sm">
                <div className="flex flex-col gap-0.5 xs:flex-row xs:items-center xs:justify-between">
                  <dt className="uppercase tracking-[0.2em] text-gold-600">Engagement</dt>
                  <dd className="text-maroon-700">Sep 12, 2026 · 6:00 – 8:00 PM</dd>
                </div>
                <div className="flex flex-col gap-0.5 xs:flex-row xs:items-center xs:justify-between">
                  <dt className="uppercase tracking-[0.2em] text-gold-600">Wedding</dt>
                  <dd className="text-maroon-700">Sep 13, 2026 · 8:00 – 9:00 AM</dd>
                </div>
              </dl>

              <motion.a
                href={MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
                className="btn-gold mt-6 rounded-full px-6 py-3 font-sans text-xs uppercase tracking-[0.25em]"
              >
                <Navigation size={14} />
                Get Directions
              </motion.a>
            </div>
          </SpotlightCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 26, rotateY: -14 }}
          whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformPerspective: 1200 }}
          className="glass gold-edge overflow-hidden rounded-3xl p-2"
        >
          <div className="relative w-full overflow-hidden rounded-2xl" style={{ paddingBottom: '75%' }}>
            <iframe
              title="Wedding Venue Location"
              src={WEDDING.mapsEmbedSrc}
              className="absolute inset-0 h-full w-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Wishes                                                              */
/* ------------------------------------------------------------------ */

function HeartShower({ onDone }) {
  const petals = React.useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2.2 + Math.random() * 1.6,
        size: 14 + Math.random() * 20,
        drift: (Math.random() - 0.5) * 120,
        rotate: (Math.random() - 0.5) * 120,
        color: ['text-rose-500', 'text-rose-400', 'text-rose-600', 'text-gold-400'][i % 4],
      })),
    []
  );

  useEffect(() => {
    const t = setTimeout(onDone, 4200);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="pointer-events-none fixed inset-0 z-[70] overflow-hidden">
      {petals.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: '-12vh', x: 0, opacity: 0, rotate: 0 }}
          animate={{ y: '112vh', x: p.drift, opacity: [0, 1, 1, 0.8], rotate: p.rotate }}
          transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }}
          style={{ position: 'absolute', left: `${p.left}%`, top: 0 }}
          className={p.color}
        >
          <Heart size={p.size} fill="currentColor" />
        </motion.span>
      ))}
    </div>
  );
}

function Wishes() {
  const tier = useTier();
  const [count, setCount] = useState(null);
  const [bursts, setBursts] = useState([]);
  const [celebrating, setCelebrating] = useState(false);
  const [taps, setTaps] = useState(0);
  const burstIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    fetch(WISH_GET_URL)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setCount(typeof data.value === 'number' ? data.value : 0);
      })
      .catch(() => {
        if (!cancelled) setCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const maybeCelebrate = (newValue) => {
    // Every 10th wish (and the very first) earns a full heart-shower celebration.
    if (newValue === 1 || newValue % 10 === 0) {
      setCelebrating(true);
    }
  };

  const sendWish = () => {
    setTaps((t) => t + 1);

    const baseId = burstIdRef.current;
    const newBursts = Array.from({ length: 3 }, (_, i) => ({
      id: baseId + i + 1,
      offsetX: (Math.random() - 0.5) * 70,
      delay: i * 0.08,
      rotate: (Math.random() - 0.5) * 40,
      scale: 0.7 + Math.random() * 0.5,
    }));
    burstIdRef.current = baseId + newBursts.length;
    setBursts((prev) => [...prev, ...newBursts]);
    setTimeout(() => {
      setBursts((prev) => prev.filter((b) => !newBursts.some((nb) => nb.id === b.id)));
    }, 1300);

    setCount((prev) => {
      const next = (prev ?? 0) + 1;
      maybeCelebrate(next);
      return next;
    });

    fetch(WISH_HIT_URL)
      .then((res) => res.json())
      .then((data) => {
        if (typeof data.value === 'number') setCount(data.value);
      })
      .catch(() => {
        /* offline or the free counter service is unreachable — keep the optimistic local count */
      });
  };

  return (
    <section
      id="wishes"
      className="relative scroll-mt-24 px-6 py-14 pb-36 md:scroll-mt-28 md:py-32 md:pb-32"
    >
      <AnimatePresence>
        {celebrating && <HeartShower onDone={() => setCelebrating(false)} />}
      </AnimatePresence>

      <SectionHeading kicker="With Love" title="Send Us Your Wishes" />
      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        <p className="mb-10 max-w-xs font-sans text-sm text-maroon-500">
          Tap the heart and send your blessings our way — every tap means the world to us.
        </p>

        <div className="relative h-44 w-44 sm:h-52 sm:w-52">
          {/* pulsing love rings */}
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <motion.span
              animate={{ scale: [1, 1.8], opacity: [0.5, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeOut' }}
              className="absolute h-28 w-28 rounded-full border-2 border-rose-300 sm:h-32 sm:w-32"
            />
            <motion.span
              animate={{ scale: [1, 1.8], opacity: [0.5, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeOut', delay: 1 }}
              className="absolute h-28 w-28 rounded-full border-2 border-rose-300 sm:h-32 sm:w-32"
            />
          </span>

          {/* the sculpted heart lives in its own canvas, sized to this box */}
          <div className="pointer-events-none absolute inset-[-18%]">
            <Canvas3D tier={tier}>
              <HeartSculpture tapCount={taps} tier={tier} />
            </Canvas3D>
          </div>

          {tier === 'off' && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <Heart size={72} className="text-rose-500 drop-glow-rose" fill="currentColor" />
            </span>
          )}

          <motion.button
            type="button"
            onClick={sendWish}
            whileTap={{ scale: 0.9 }}
            aria-label="Tap to send a wish"
            className="absolute inset-0 z-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
          />

          <AnimatePresence>
            {bursts.map((b) => (
              <motion.span
                key={b.id}
                initial={{ opacity: 1, y: 0, x: b.offsetX, scale: 0.3, rotate: 0 }}
                animate={{ opacity: 0, y: -110, scale: b.scale, rotate: b.rotate }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, delay: b.delay, ease: 'easeOut' }}
                className="pointer-events-none absolute left-1/2 top-4 z-20 -translate-x-1/2 text-rose-500"
              >
                <Heart size={16} fill="currentColor" />
              </motion.span>
            ))}
          </AnimatePresence>
        </div>

        <p className="mt-6 font-sans text-xs uppercase tracking-[0.3em] text-rose-500">
          Tap to Wish
        </p>

        <div className="glass gold-edge mt-8 rounded-2xl px-10 py-5">
          <AnimatePresence mode="wait">
            <motion.span
              key={count}
              initial={{ opacity: 0, y: -8, scale: 1.3 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, type: 'spring' }}
              className="block font-serif text-4xl font-semibold text-rose-600"
            >
              {count === null ? '—' : count.toLocaleString()}
            </motion.span>
          </AnimatePresence>
          <span className="mt-1 block font-sans text-[10px] uppercase tracking-[0.25em] text-gold-600">
            Wishes from everyone
          </span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 w-full"
        >
          <OrnamentDivider />
          <div className="mt-6">
            <ShareInvite
              message={`${WEDDING.groom} & ${WEDDING.bride} are getting married on Sunday, 13 September 2026 at ${WEDDING.venueName}, ${WEDDING.venueAddress}. Together with their families, do join us.`}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Footer                                                              */
/* ------------------------------------------------------------------ */

function Footer() {
  return (
    <footer className="relative overflow-hidden border-t border-gold-300/30 px-6 pb-32 pt-14 text-center md:pb-16 md:pt-20">
      {/* a faint oversized monogram behind the sign-off */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-script text-[11rem] leading-none text-maroon-700/[0.04] md:text-[15rem]"
      >
        R&amp;H
      </span>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold-600">
          Until we meet
        </p>
        <p className="mt-3 font-script text-4xl text-maroon-700 sm:text-5xl">
          {WEDDING.groom} &amp; {WEDDING.bride}
        </p>

        <Flourish />

        <p className="font-serif text-base italic text-maroon-600/90 sm:text-lg">
          "We can't wait to celebrate with you."
        </p>
        {/* அன்புடன் — "with love"; renders via the OS's Tamil system font */}
        <p className="mt-2 font-sans text-lg text-rose-500">அன்புடன்</p>

        <p className="mt-8 flex items-center justify-center gap-2 font-sans text-[10px] uppercase tracking-[0.3em] text-maroon-400">
          13 · 09 · 2026
          <Heart size={10} className="animate-heartbeat text-rose-500" fill="currentColor" />
          Natham, Dindigul
        </p>
      </motion.div>
    </footer>
  );
}

/* ------------------------------------------------------------------ */
/*  App                                                                 */
/* ------------------------------------------------------------------ */

const SECTION_IDS = NAV_ITEMS.map((item) => item.id);

export default function App() {
  const tier = usePerfTier();
  const [active, setActive] = useState('home');
  const [loading, setLoading] = useState(true);
  const [taps, setTaps] = useState([]);
  const tapIdRef = useRef(0);

  useEffect(() => {
    startViewerTracking();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Keep the page pinned to the top while the preloader is up.
  useEffect(() => {
    document.body.style.overflow = loading ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [loading]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBackgroundClick = (e) => {
    if (e.target.closest('button, a, input, textarea, [role="button"], [role="dialog"]')) return;
    const id = tapIdRef.current + 1;
    tapIdRef.current = id;
    setTaps((prev) => [...prev, { id, x: e.clientX, y: e.clientY }]);
    setTimeout(() => {
      setTaps((prev) => prev.filter((t) => t.id !== id));
    }, 900);
  };

  return (
    <TierContext.Provider value={tier}>
      <div
        className="relative min-h-screen font-sans text-maroon-700"
        onClick={handleBackgroundClick}
      >
        <AnimatePresence>{loading && <Preloader onDone={() => setLoading(false)} />}</AnimatePresence>

        <ScrollProgress />
        <Backdrop />
        <TopNav active={active} onNavigate={scrollToSection} />
        <MobileMonogram />

        <main className="relative z-10">
          <Hero onNavigate={scrollToSection} />
          <Blessings />
          <Assets />
          <Moments />
          <Location />
          <Wishes />
        </main>

        <Footer />
        <BottomNav active={active} onNavigate={scrollToSection} />

        <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden">
          <AnimatePresence>
            {taps.map((t) => (
              <motion.span
                key={t.id}
                initial={{ opacity: 1, y: 0, scale: 0.5 }}
                animate={{ opacity: 0, y: -60, scale: 1.1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
                style={{ position: 'absolute', left: t.x - 10, top: t.y - 10 }}
                className="text-rose-500"
              >
                <Heart size={20} fill="currentColor" />
              </motion.span>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </TierContext.Provider>
  );
}
